/**
 * NovaProvidersCard — manages NovaAI providers from Settings → NovaAI tab.
 *
 * Pulls /admin/nova/providers for list/CRUD. The admin registers OpenAI /
 * Anthropic / Gemini / OpenRouter / custom OpenAI-compatible endpoints with
 * their own API keys and marks one as default. The default is used by any
 * Nova chat session that doesn't override the provider.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Settings, Check, Loader2, Pencil, Trash2, Bot, Zap, Key, Plus,
} from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Field } from "./Fields";

const PRESETS = [
  { name: "OpenAI GPT-5.2", provider_type: "openai", model: "gpt-5.2",
    base_url: "https://api.openai.com/v1", hint: "platform.openai.com/api-keys" },
  { name: "OpenAI GPT-5.6 Terra", provider_type: "openai", model: "gpt-5.6-terra",
    base_url: "https://api.openai.com/v1", hint: "platform.openai.com/api-keys" },
  { name: "OpenAI GPT-4.1", provider_type: "openai", model: "gpt-4.1",
    base_url: "https://api.openai.com/v1", hint: "platform.openai.com/api-keys" },
  { name: "Claude Sonnet 5.5", provider_type: "anthropic", model: "claude-sonnet-5-5-latest",
    base_url: "", hint: "console.anthropic.com → API Keys" },
  { name: "Claude Opus 4.8", provider_type: "anthropic", model: "claude-opus-4-8-latest",
    base_url: "", hint: "console.anthropic.com → API Keys" },
  { name: "Gemini 3 Pro", provider_type: "gemini", model: "gemini-3-pro-latest",
    base_url: "", hint: "aistudio.google.com → Get API key" },
  { name: "Gemini 3 Flash", provider_type: "gemini", model: "gemini-3-flash-latest",
    base_url: "", hint: "aistudio.google.com → Get API key" },
  { name: "DeepSeek (via OpenRouter)", provider_type: "openrouter", model: "deepseek/deepseek-chat",
    base_url: "https://openrouter.ai/api/v1", hint: "openrouter.ai/keys" },
  { name: "Mistral Large (via OpenRouter)", provider_type: "openrouter", model: "mistralai/mistral-large-latest",
    base_url: "https://openrouter.ai/api/v1", hint: "openrouter.ai/keys" },
  { name: "Groq Llama 3.3 70B", provider_type: "openrouter", model: "meta-llama/llama-3.3-70b-instruct",
    base_url: "https://openrouter.ai/api/v1", hint: "openrouter.ai/keys" },
  { name: "Özel sağlayıcı", provider_type: "custom", model: "",
    base_url: "", hint: "OpenAI uyumlu base URL girin" },
];

export default function NovaProvidersCard() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "", provider_type: "openai", model: "",
    base_url: "", api_key: "", is_default: false,
  });
  const [busy, setBusy] = useState(false);

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["nova", "providers"],
    queryFn: () => api.get("/admin/nova/providers").then((r) => r.data),
  });

  const reset = () => {
    setEditing(null);
    setForm({ name: "", provider_type: "openai", model: "", base_url: "", api_key: "", is_default: false });
  };

  const invalidate = () => qc.invalidateQueries({ queryKey: ["nova", "providers"] });

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, name: p.name, provider_type: p.provider_type,
                      model: p.model, base_url: p.base_url }));
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Sağlayıcı adı gerekli"); return; }
    if (!form.model.trim() && form.provider_type !== "custom") {
      toast.error("Model adı gerekli"); return;
    }
    setBusy(true);
    try {
      const payload = { ...form, name, model: form.model.trim() };
      if (editing) {
        await api.put(`/admin/nova/providers/${editing.id}`, payload);
        toast.success("Sağlayıcı güncellendi");
      } else {
        await api.post("/admin/nova/providers", payload);
        toast.success("Sağlayıcı eklendi");
      }
      reset();
      invalidate();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const beginEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", provider_type: p.provider_type || "openai",
      model: p.model || "", base_url: p.base_url || "",
      api_key: "", // leaving it blank preserves the stored key
      is_default: !!p.is_default,
    });
  };

  const remove = async (p) => {
    if (!window.confirm(`"${p.name}" sağlayıcısı silinecek. Onaylıyor musunuz?`)) return;
    try { await api.delete(`/admin/nova/providers/${p.id}`); invalidate(); toast.success("Silindi"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const setDefault = async (p) => {
    try {
      await api.put(`/admin/nova/providers/${p.id}`, { is_default: true });
      invalidate();
      toast.success(`${p.name} varsayılan sağlayıcı`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="settings-nova">
      <div className="bg-white border border-indigo-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-indigo-50 to-violet-50 flex items-center gap-2">
          <Settings size={18} className="text-indigo-600" />
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">NovaAI Sağlayıcıları</h2>
            <p className="text-xs text-stone-500 mt-0.5">
              Workspace &gt; NovaAI editöründe kullanılacak modelleri burada tanımlayın.
              API anahtarları şifrelenerek saklanır, panele yalnızca son 4 karakteri döner.
            </p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Add / edit form */}
          <div className="bg-indigo-50/40 border border-indigo-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-indigo-900 uppercase tracking-wider">
                {editing ? "Sağlayıcıyı düzenle" : "Yeni sağlayıcı"}
              </span>
              {editing && (
                <button onClick={reset} className="text-xs text-indigo-700 hover:underline"
                        data-testid="nova-reset-edit">
                  Yeni ekleme moduna dön
                </button>
              )}
            </div>

            {!editing && (
              <div className="mb-3">
                <label className="text-[11px] font-semibold text-stone-600 mb-1 block">Hızlı seç</label>
                <div className="flex flex-wrap gap-1.5">
                  {PRESETS.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => applyPreset(p)}
                      className="text-[11px] rounded-full border border-indigo-200 bg-white px-2.5 py-1 hover:bg-indigo-100 text-indigo-800"
                      data-testid={`nova-preset-${p.provider_type}-${p.model || "custom"}`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-stone-500 mt-2 flex items-start gap-1">
                  <Zap size={11} className="shrink-0 mt-0.5 text-amber-500" />
                  Hazır şablonu seçin, ardından sadece API Key'inizi girip "Ekle"ye basın.
                </p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <Field label="İsim">
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       placeholder="Örn. OpenAI GPT-5.2" data-testid="nova-pm-name" />
              </Field>
              <Field label="Tür">
                <select value={form.provider_type}
                        onChange={(e) => setForm((f) => ({ ...f, provider_type: e.target.value }))}
                        className="w-full h-9 border rounded-md px-3 text-sm bg-white"
                        data-testid="nova-pm-type">
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic Claude</option>
                  <option value="gemini">Google Gemini</option>
                  <option value="openrouter">OpenRouter</option>
                  <option value="custom">Özel (OpenAI uyumlu)</option>
                </select>
              </Field>
              <Field label="Model">
                <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
                       placeholder="gpt-5.2 / claude-sonnet-5-5-latest / gemini-3-pro-latest"
                       data-testid="nova-pm-model" />
              </Field>
              <Field label="Base URL (opsiyonel)">
                <Input value={form.base_url} onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))}
                       placeholder="otomatik"
                       data-testid="nova-pm-base" />
              </Field>
            </div>

            <Field label={editing ? "API Key (boş bırakılırsa mevcut korunur)" : "API Key"}>
              <div className="relative">
                <Key size={14} className="absolute left-3 top-2.5 text-stone-400" />
                <Input value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
                       type="password" autoComplete="new-password" placeholder="sk-..." className="pl-9"
                       data-testid="nova-pm-key" />
              </div>
            </Field>

            <label className="flex items-center gap-2 mt-2 text-xs">
              <input type="checkbox" checked={form.is_default}
                     onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))}
                     data-testid="nova-pm-default" />
              Varsayılan sağlayıcı olarak işaretle
            </label>

            <div className="flex items-center gap-2 mt-3">
              <Button size="sm" onClick={save} disabled={busy} data-testid="nova-pm-save">
                {busy
                  ? <Loader2 size={13} className="animate-spin mr-1" />
                  : (editing ? <Check size={13} className="mr-1" /> : <Plus size={13} className="mr-1" />)}
                {editing ? "Güncelle" : "Ekle"}
              </Button>
            </div>
          </div>

          {/* List */}
          <div>
            <div className="text-xs font-semibold text-stone-500 mb-2 uppercase tracking-wider">
              Kayıtlı sağlayıcılar ({providers.length})
            </div>
            {isLoading ? (
              <p className="text-sm text-stone-400 italic text-center py-6">Yükleniyor…</p>
            ) : providers.length === 0 ? (
              <p className="text-sm text-stone-400 italic text-center py-6">Henüz sağlayıcı yok.</p>
            ) : (
              <ul className="space-y-2" data-testid="nova-provider-list">
                {providers.map((p) => (
                  <li key={p.id}
                      className={`border rounded-xl p-3 flex items-center gap-3 ${
                        p.is_default ? "border-emerald-300 bg-emerald-50/40" : "border-stone-200 bg-white"
                      }`}
                      data-testid={`nova-provider-${p.id}`}>
                    <Bot size={20} className={p.is_default ? "text-emerald-600" : "text-stone-500"} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">{p.name}</span>
                        {p.is_default && (
                          <span className="text-[10px] bg-emerald-500 text-white rounded-full px-2 py-0.5 font-semibold">
                            VARSAYILAN
                          </span>
                        )}
                        <span className="text-[10px] bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5">
                          {p.provider_type}
                        </span>
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5 truncate">
                        <code className="text-indigo-700">{p.model || "—"}</code>
                        {p.has_key ? (
                          <span className="ml-2 text-emerald-700">· 🔑 {p.api_key_mask}</span>
                        ) : (
                          <span className="ml-2 text-red-600">· API key yok</span>
                        )}
                      </div>
                    </div>
                    {!p.is_default && (
                      <button onClick={() => setDefault(p)} className="text-xs text-emerald-700 hover:underline"
                              data-testid={`nova-pm-setdefault-${p.id}`}>
                        Varsayılan yap
                      </button>
                    )}
                    <button onClick={() => beginEdit(p)}
                            className="p-1.5 rounded hover:bg-indigo-50 text-indigo-700"
                            data-testid={`nova-pm-edit-${p.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => remove(p)}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            data-testid={`nova-pm-del-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
